"use client";

import { useMemo } from "react";

import {
  PageSizeProvider,
  Pagination,
  usePageSize,
  useRouterParams,
} from "@zapier/shared";
import { useFolderTree, useQuerySearchZaps } from "@zapier/client";
import { FoldersOrder } from "@zapier/bff";
import { DEFAULT_FOLDER_ID, FolderTypeQueryState } from "@zapier/utils";
import { SortDescriptor } from "@zapier/design-system";

import { EmptyState } from "./FoldersEmptyState";
import { FoldersHeader } from "./FoldersHeader/FoldersHeader";
import { FolderTable } from "./FolderTable";

function FoldersWithPageSize() {
  const {
    page,
    search: initialSearchValue,
    folderType,
    owner: selectedOwner,
    order,
    setParams,
  } = useRouterParams();

  const { size: pageSize } = usePageSize();

  const folderTypeParameter = useMemo(() => {
    // return personal folder type if folderType is not defined by default
    // personal is stripped from the URL in useRouterParams
    if (!folderType) {
      return FolderTypeQueryState.All;
    }

    return folderType as FolderTypeQueryState;
  }, [folderType]);

  const sortChange = (descriptor: SortDescriptor) => {
    const orderChange = `${descriptor.direction === "ascending" ? "" : "-"}${descriptor.column}`;
    if (orderChange !== order) {
      setParams({ order: orderChange, page: 1 });
    }
  };

  const { isFoldersLoading, totalPages, paginatedFolders, totalResults } =
    useFolderTree({
      page,
      pageSize,
      searchQuery: initialSearchValue || "",
      folderType: folderTypeParameter,
      selectedOwner: selectedOwner ? Number.parseInt(selectedOwner) : undefined,
      order: order ? (order as FoldersOrder) : undefined,
      includeDefaultFolder: true,
    });

  const { response, isLoading: isSearchZapsLoading } = useQuerySearchZaps({
    search: "",
    page: 1,
    pageSize: 1,
    owner: "",
    status: "all",
    enabled: true,
  });

  const isFoldersOrZapsLoading = isFoldersLoading || isSearchZapsLoading;

  const hasZaps = (response?.count || 0) > 0;

  const onlyHasDefaultFolder =
    paginatedFolders.length === 1 &&
    paginatedFolders[0].id === DEFAULT_FOLDER_ID;

  // User has folders that are not the default folder
  const hasFolders = paginatedFolders.length > 0 && !onlyHasDefaultFolder;

  // Show the empty state if the user has no folders and no zaps and the split treatment is on.
  // Don't show empty state when user is actively searching or filtering (initialSearchValue or selectedOwner search params do not exist).
  const showEmptyState =
    !isFoldersOrZapsLoading &&
    !hasZaps &&
    !hasFolders &&
    !initialSearchValue &&
    !selectedOwner;

  // We show the folders table if zaps/folders is loading to handle skeleton loading view.
  // If loading is done, we show the folders table if there are folders or zaps.
  // Also show the table when user is actively searching or filtering to display "No results found".

  const showFoldersTable =
    isFoldersOrZapsLoading ||
    hasFolders ||
    hasZaps ||
    initialSearchValue ||
    selectedOwner;

  return (
    <>
      {/* Always show header so Zaps/Folders navigation is accessible */}
      <FoldersHeader
        totalResults={totalResults}
        isLoading={isFoldersOrZapsLoading}
      />

      {showEmptyState && <EmptyState />}

      {showFoldersTable && (
        <>
          <FolderTable
            ariaLabel="folders-list"
            folders={paginatedFolders}
            folderType={folderType as FolderTypeQueryState}
            isFoldersExpandedByDefault={!!initialSearchValue || !!selectedOwner}
            isLoading={isFoldersOrZapsLoading}
            onSortChange={sortChange}
            order={(order && (order as FoldersOrder)) || undefined}
            searchQuery={initialSearchValue}
          />

          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalResults}
            isLoading={isFoldersLoading}
          />
        </>
      )}
    </>
  );
}

/**
 * Wrap this component in PageSizeProvider so that the context is available
 * to the Table and Pagination components above.
 */
export function Folders() {
  return (
    <div>
      <PageSizeProvider>
        <FoldersWithPageSize />
      </PageSizeProvider>
    </div>
  );
}
